<script lang="ts">
import type { AuthenticateWithPassword, ChangePasswordInput, LoginInput, PersistentUserSession } from '@mimir/client-common/util/persistentUserSession';
import { hasRouteAccess, redirectBackFromLoginPage } from './routerActions';
import { initializeStore } from './store/initializeStore';
import type { ServiceCache } from '@mimir/common/client/serviceCache';
import { invariant } from '@mimir/common/util/invariant';
import type { AccountInput, AccountState, ChangePasswordState } from './appEventTypes';
import type { UpdateUserDto, VerifyUserDto } from '@mimir/common/dto/userDto';
import { AccountStatus } from '@mimir/common/dto/userDto';
import type { RawLocation, VueRouter } from 'vue-router/types/router';
import { generateUserLoggedInJSON } from './util/postMessageHelpers';
import { exceptionSummary } from '@mimir/common/util/exception';
import type { User } from './model/User';
import { postAvatar } from './model/User';
import { TOAST, showToast } from './eventBus';
import noop from 'lodash/noop';
import Vue from 'vue';
import type { PropType } from '@mimir/web/compat/vueCompat';
import { defineComponent } from '@mimir/web/compat/vueCompat';
import { api_updateUser } from '@mimir/client-common/util/api';
import { dynamicApiClient } from '@mimir/client-common/aws/api/http';
import type { BackendClient } from '@mimir/common/util/backendClient';
import type { AppRoutingState } from '@mimir/web/sharedState/appRoutingState';
import { isInAdobeApp, type ApplicationContext } from '@mimir/common/context/applicationContext';
import { isProdOrStgStage } from '@mimir/web/util/stageUtils';
import type { UploadItem } from '@mimir/client-common/upload/UploadItem';
import { getLogOutRedirectSession } from './util/sessionStorage';
import { clearSessionStorageGlobalLoginData, validateComingFromStageQueryValue } from './util/globalLogin';
import type { BasicUploadManager } from '@mimir/common/upload/BasicUploadManager';
import type { QuickPreview } from '@mimir/web/model/quickPreview';
import { getDefaultFilterRedirectIfNeeded, shouldUseDefaultFilterOrBookmark } from './util/filterUtils';
import { tryGetDefaultBookmarkLocation } from './util/bookmarkTree';
import type { PlayerServices } from './model/playerServices';
import type { OpenInQuickPreview } from './model/openInQuickPreview';
import type { ClientItem } from '@mimir/common/dto/searchResultHit';
import type { ItemDetailsState } from './model/itemDetailsState';
import type { ItemPollingOps } from '@mimir/client-common/polling/itemPollingOps';
import { searchState } from './globals/searchState';


// Dev Tools should be enabled for non production (productions and staging) environment.
invariant(typeof __USE_IMPORT_META__ !== 'undefined', 'Unexpected execution enviornment without verified import.meta');

Vue.config.devtools = !isProdOrStgStage(import.meta.env);

export default defineComponent({
  name: 'App',
  props: {
    applicationContext: {
      type: Object as PropType<ApplicationContext>,
      required: true,
    },
    session: {
      type: Object as PropType<PersistentUserSession>,
      required: true
    },
    playerServices: {
      type: Object as PropType<PlayerServices>,
      required: true,
    },
    serviceCache: {
      type: Object as PropType<ServiceCache>,
      required: true
    },
    appRoutingState: {
      type: Object as PropType<AppRoutingState>,
      required: true
    },
    uploadManager: {
      type: Object as PropType<BasicUploadManager>,
      required: true,
    },
    quickPreview: {
      type: Object as PropType<QuickPreview>,
      required: true,
    },
    itemPollingOps: {
      type: Object as PropType<ItemPollingOps>,
      required: true,
    },
    itemDetailsState: {
      type: Object as PropType<ItemDetailsState>,
      required: true,
    },
  },
  data() {
    return {
      accountState: { busy: false, error: null } as AccountState,
      changePasswordState: { busy: false, error: null } as ChangePasswordState,
      restarting: false,
      listener: null as ((this: Window, ev: MessageEvent) => void) | null,
      fromDocs: '',
    };
  },
  computed: {
    openInQuickPreview(): OpenInQuickPreview {
      return (item: ClientItem) => {
        this.quickPreview.ops.loadItemInQuickPreview({ item, searchState });
        const query = this.$route.query;
        this.$router.push({ query, path: '/search-item' });
      };
    },
    preventBrowserNavigationWarning(): boolean {
      return this.$store.getters.preventBrowserNavigationWarning;
    },
    ongoingUploadsLength(): number {
      return this.$store.getters.ongoingUploads.filter((item: UploadItem) => item.isUploading).length;
    },
    user(): User {
      return this.$store.getters.user;
    },
  },
  watch: {
    ongoingUploadsLength(newLength) {
      this.$store.commit('preventBrowserNavigationWarning', newLength === 0);
    },
    user() {
      if (this.fromDocs && this.user) {
        window.location.href = window.location.origin + this.fromDocs;
      }
    },
  },
  mounted() {
    invariant(this.session != null, 'App needs session');
    invariant(this.serviceCache != null, 'App needs serviceCache');
    invariant(this.uploadManager !== undefined, 'App needs uploadManager');
    /**
     * This will cause browsers to prompt when users attempt to leave the
     * application whether they're sure or not. We want this to be global,
     * so we add it on app mount.
     *
     * Due to this being possibly annoying during development, we only load this in
     * non development builds.
     */
    if (!Vue.config.devtools && !isInAdobeApp(this.applicationContext)) {
      window.addEventListener('beforeunload', ev => {
        if (!this.preventBrowserNavigationWarning) {
          // Text is mostly placeholder- modern browsers do not actually display custom
          // messages anymore, so the content is practically irrelevant.
          ev.returnValue = 'Are you sure you want to leave the page?';
        }
      });
    }
    (window as any).session = this.session;

    this.fromDocs = new URL(window.location.href).searchParams.get('fromDocs') ?? '';

    this.listener = (ev: MessageEvent) => {
      if (ev.data.type == 'AuthenticateWithQueryParameters') {
        if (!ev.origin || ev.origin != window.location.origin) {
          this.accountState.error = 'Rejected authentication from foreign origin ' + ev.origin;
          return;
        }

        const accountInput: AccountInput = {
          login: ev.data
        };
        this.tryHandleAccountInput(accountInput);
      }
    };
    window.addEventListener('message', this.listener);
  },
  beforeDestroy() {
    if (this.listener) {
      window.removeEventListener('message', this.listener);
      this.listener = null;
    }
  },
  methods: {
    tryHandleAccountInput(input: AccountInput) {
      if (this.accountState.busy) return;
      this.accountState.busy = true;
      if (input.login?.type === 'RedirectToIdentityProvider') {
        this.$store.commit('preventBrowserNavigationWarning', true);
      }
      this.handleAccountInput(input).catch(ex => console.warn('App account input failure', ex)).finally(
        () => {
          this.accountState.busy = false;
          this.clearQueryStringParams();
        }
      );
    },
    clearQueryStringParams() {
      const currentUrl = new URL(window.location.href);
      const hasEmail = currentUrl.searchParams.has('email');
      const hasOrganizationId = currentUrl.searchParams.has('organizationId');

      // Only proceed if there are query params to remove
      if (!hasEmail && !hasOrganizationId) {
        return;
      }

      currentUrl.searchParams.delete('email');
      currentUrl.searchParams.delete('organizationId');
      const newUrl = currentUrl.toString();
      const updatedQuery = { ...this.$route.query };
      delete updatedQuery.email;
      delete updatedQuery.organizationId;
      void this.$router.replace({ query: updatedQuery }).catch(() => {
        // Ignore navigation errors (e.g., redundant navigation)
      });
      window.history.replaceState({}, document.title, newUrl);
    },
    async onChangePassword(input: ChangePasswordInput) {
      this.changePasswordState.busy = true;
      try {
        await this.session.changePassword({ oldPassword: input.oldPassword, newPassword: input.newPassword });
        showToast('Successfully updated password.', TOAST.SUCCESS);
        this.$router.push('/user-settings').catch(noop);
      } catch (err: any) {
        if (err?.code === 'NotAuthorizedException') {
          this.changePasswordState.error = 'Incorrect password';
        } else {
          this.changePasswordState.error = err?.message as string;
        }
        console.error('Change password failed.', err);
      } finally {
        this.changePasswordState.busy = false;
      }
    },
    onClearLoginState() {
      this.session.signOut();
    },
    onResetChangePasswordState() {
      this.changePasswordState = { busy: false, error: null };
    },
    async handleAccountInput(input: AccountInput) {
      if (input.login) {
        const phaseType = await handleLoginInput(this.session, this.$router, input.login);
        if (phaseType != 'AuthenticationCompleted') {
          return;
        }
      }

      if (input.profilePicture) {
        const client = await this.getBackendClient();
        await postAvatar(client, input.profilePicture, () => { });
        if (this.appRoutingState.storeInitialized) {
          this.$store.commit('injectFakeAvatar', URL.createObjectURL(input.profilePicture));
        }
      }

      if (input.userUpdate) {
        await this.updateUserEntity(input.userUpdate);
        if (input.userUpdate.accountStatus == AccountStatus.Verified) {
          await this.reloadAfterAccountVerification(input.login ?? null);
          return;
        }
      }

      invariant(this.session.loginState.refreshToken != null, 'Expected still to be signed in.');

      if (input.signOut) {
        this.restarting = true;
        /*
          We must enable preventBrowserNavigationWarning even when store is
          not initialized.
        */
        this.$store.commit('preventBrowserNavigationWarning', true);

        const user: User = this.$store.getters.user;
        const organizationToRedirectTo = user.isExternal ? user.tenantId : undefined;

        const comingFromStage = getLogOutRedirectSession();
        if (comingFromStage && validateComingFromStageQueryValue(comingFromStage)) {
          clearSessionStorageGlobalLoginData();
        }
        await this.session.redirectToSignOut(comingFromStage, organizationToRedirectTo);
        return;
      }

      await this.initializeStoreIfNeeded();
      if (this.fromDocs) window.location.href = window.location.origin + this.fromDocs;
    },
    /*
      The regular backend client requires an initialized Vuex store to perform
      API calls. However in some cases the store cannot be initialized yet
      because we haven't verified the user account yet. Therefore we create this
      backend client that does not require the Vuex store in order to be able to
      update the user.

      TODO: Now that we've started pulling the backend client out of Vuex and
      into clients/mimir-common/src/globals/backendClient.ts we may be able to
      refactor the bootstrapping to avoid the circular initialization
      dependencies in a better way.
    */
    async getBackendClient(): Promise<BackendClient> {
      const sdoc = await this.serviceCache.getServiceDocument();
      return {
        getApiClient() {
          return dynamicApiClient();
        },
        getServiceLinks() {
          return sdoc.links;
        }
      };
    },
    async updateUserEntity(userUpdate: VerifyUserDto) {
      /*
        TODO: Get rid of use of email and id claims here. The updateUser API
        handler does not use the email or id properties of the UserDto type at
        all. We're only providing these properties here because the current API
        has been defined as requiring them.
      */
      const email = this.session.loginState.claims.email;
      invariant(email, 'Expected email claim after authentication');
      const id = this.session.loginState.claims.sub;
      invariant(id, 'Expected sub claim after authentication');
      const userDto: UpdateUserDto = {
        ...userUpdate,
        email,
        id
      };

      try {
        this.accountState.busy = true;
        this.accountState.error = null;

        const client = await this.getBackendClient();
        await api_updateUser(client, userDto);

      } catch (ex) {
        this.accountState.error = exceptionSummary(ex);
        throw ex;
      } finally {
        this.accountState.busy = false;
      }
    },
    async reloadAfterAccountVerification(login: LoginInput | null) {
      /*
        User updates has affected authentication tokens, we need to sign out
        and back in again (is possible) to have them take effect.

        A failure to do so will result in half-backed credentials which
        cause some operations to fail.
      */
      const username = this.session.user?.getUsername();
      invariant(username, 'Expected username before signOut');

      this.restarting = true;
      await this.session.signOut();
      if (login?.type == 'NewPassword') {
        /*
          When we've just changed the password we can use the new password
          to log back in automatically.
        */
        const relogin: AuthenticateWithPassword = {
          type: 'AuthenticateWithPassword',
          username,
          password: login.newPassword
        };

        await handleLoginInput(this.session, this.$router, relogin);

        /*
          If login succeeded, then we'll at this point have a persistent
          refresh token. Rather than trying to update the store with state
          based on the new credentials, we instead reload the whole page to
          kick off a fresh initialization to be on the safe side.
        */
      }
      /*
        We reload regardless of whether the re-login was successful, since
        we can't continue in the existing state anyway.
      */

      /*
        We must enable preventBrowserNavigationWarning even when store is
        not initialized.
      */
      this.$store.commit('preventBrowserNavigationWarning', true);
      window.location.reload();
    },
    async initializeStoreIfNeeded() {
      if (!this.appRoutingState.storeInitialized) {
        /*
          We're intentionally breaking the rule that components should not
          mutate their own properties here by calling initializeStore which
          modifies its appRoutingState parameter.

          This is done because:
           * The App is the top-level Vue component, and thus does not have a
             parent component that could do the state modification instead.
           * The application routing state needs to be shared between the router
             and the App component.
           * The router beforeEach hook requiring the state needs to be created
             before the data of the App component has been initialized.
        */
        await initializeStore(
          this.serviceCache,
          this.session,
          this.$store,
          this.appRoutingState,
          this.$router.currentRoute,
          this.applicationContext,
          this.quickPreview,
          this.itemDetailsState,
          this.playerServices,
        );
      }

      // After login, check if we need to redirect to default bookmark or apply default filters
      if (this.appRoutingState.storeInitialized && shouldUseDefaultFilterOrBookmark(this.$route)) {
        // Try to navigate to default bookmark
        if (this.user && this.user.primaryGroupDefaultBookmarkId) {
          const defaultBookmarkLocation = await tryGetDefaultBookmarkLocation(this.user);
          if (defaultBookmarkLocation) {
            await this.$router.push(defaultBookmarkLocation).catch(noop);
            return;
          }
        }

        // Check if we need to apply default filters for search routes
        const defaultFilterRedirect = getDefaultFilterRedirectIfNeeded(this.$router.currentRoute, this.$store);
        if (defaultFilterRedirect) {
          await this.$router.push(defaultFilterRedirect).catch(noop);
          return;
        }
      }

      if (this.appRoutingState.storeInitialized) {
        const redirectRoute = await redirectBackFromLoginPage(this.$router);
        if (!hasRouteAccess(this.applicationContext, redirectRoute, this.$store)) {
          this.$router.push('/');
        }
      }
    },
  },
});

async function handleLoginInput(session: PersistentUserSession, router: VueRouter, login: LoginInput) {
  const phase = await session.handleLoginInput(login);
  if (phase?.type == 'AuthenticationCompleted') {
    // If we detect we're in an iframe, let's send a message that the user has logged in.
    // Used by Vimond IO to show tooltips on first login.
    if (window.location !== window.parent.location) {
      window.parent.postMessage(generateUserLoggedInJSON(), '*');
    }
  } else {
    /*
      The operation succeeded, but did not lead to a completed
      authentication. The loginState has been updated, so that the UI will
      present the appropriate UI state.

      For example, if the user needs to provide a new password during the
      first login, this will be represented as a challenge in the
      loginState.

      For successful processing of some input, we also want to move to a new
      page.
    */
    const routeFlow: Partial<Record<LoginInput['type'], RawLocation>> = {
      ForgotPassword: { name: 'forgotPasswordVerify' },
      ConfirmForgotPassword: { name: 'forgotPasswordReset' }
    };
    const nextLocation = routeFlow[login.type];
    if (nextLocation) {
      await router.push(nextLocation);
    }
  }
  return phase?.type;
}
</script>

<template>

  <router-view
    :login-state="session.loginState"
    :account-state="accountState"
    :change-password-state="changePasswordState"
    :restarting="restarting"
    :app-routing-state="appRoutingState"
    :applicationContext="applicationContext"
    :itemDetailsState="itemDetailsState"
    :quickPreview="quickPreview"
    :uploadManager="uploadManager"
    :openInQuickPreview="openInQuickPreview"
    :itemPollingOps="itemPollingOps"
    @account-input="tryHandleAccountInput"
    @do-change-password="onChangePassword"
    @clear-login-state="onClearLoginState"
    @reset-change-password-state="onResetChangePasswordState"
    @clear-query-params="clearQueryStringParams"
  />
</template>
